import { dataMixed } from "api/data";
import { Device } from "api/types/device";
import { DataReqBody } from "api/types/data";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeviceConfiguration } from "models/devices/configuration";
import DeviceCard from "widgets/cards/device";
import BarGraph from "widgets/charts/bar";
import { ChartDataset } from "widgets/charts/models";
import DatePickerField from "widgets/shared/inputs/datePicker";
import GenericButton from "widgets/controls/buttons/generic";
import PropagateLoader from "react-spinners/PropagateLoader";
import NoDataSvg from "assets/svg/no-data.svg";
import { formatDateToZ, formatDateTimezone } from "util/datelib";
import { getChartColors } from "util/device";
import {
  buildGatewayChartSeries,
  GatewayAggregateRow,
  GatewayChartFilter,
} from "util/gatewayAggregateChart";

const GATEWAY_CHART_BUTTONS: {
  labelKey: string;
  tooltipKey: string;
  filter: GatewayChartFilter;
}[] = [];

interface GatewayChartProps {
  device: Device;
  config: DeviceConfiguration;
  height: number;
}

const GatewayUptimeChart: React.FC<GatewayChartProps> = ({
  device,
  config,
  height,
}) => {
  const { t } = useTranslation();
  const [date, setDate] = useState(() => new Date());
  const [filter, setFilter] = useState<GatewayChartFilter>({
    calendar: "hour",
    xFormat: "day",
  });
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<ChartDataset>({
    labels: [],
    datasets: [],
  });
  const [valueScale, setValueScale] = useState<"binary" | "percent">("percent");

  const applyFilterPreset = (preset: GatewayChartFilter) => {
    if (preset.calendar === "hour" || preset.calendar === "month") {
      setDate(new Date());
    }
    setFilter(preset);
  };

  const fetchData = useCallback(async () => {
    if (!device?.snr) return;
    const { color0 } = getChartColors(config);
    setLoading(true);
    const from = formatDateToZ(date, false, filter.xFormat, device.timezone);
    const to = formatDateToZ(date, true, filter.xFormat, device.timezone);

    const req: DataReqBody = {
      snr: device.snr,
      from,
      to,
      bucket: "hour",
    };

    const { data, status, error } = await dataMixed(req);
    setLoading(false);

    if (error || status !== 200 || !Array.isArray(data)) {
      setChartData({ labels: [], datasets: [] });
      setValueScale("percent");
      return;
    }

    const list = data as GatewayAggregateRow[];

    const series = buildGatewayChartSeries(filter, date, device.timezone, list);
    setValueScale(series.valueScale);
    const unit =
      series.valueScale === "percent"
        ? t("device.gateway.chart.unit.percent")
        : t("device.gateway.chart.unit.data");

    setChartData({
      labels: series.labels,
      datasets: [
        {
          label:
            series.valueScale === "percent"
              ? t("device.gateway.chart.series.uptime")
              : t("device.gateway.chart.series.hourly"),
          data: series.values,
          backgroundColor: color0,
          borderWidth: 2,
          borderRadius: 5,
          borderSkipped: false,
          unit,
        },
      ],
    });
  }, [
    config,
    date,
    device?.snr,
    device?.timezone,
    filter.calendar,
    filter.xFormat,
    t,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getDescription = () => {
    if (filter.calendar === "hour") {
      return formatDateTimezone(
        formatDateToZ(date, false, "day", device?.timezone),
        "ddMMMyyyy",
        device?.timezone
      );
    }
    if (filter.calendar === "month") {
      return formatDateTimezone(
        formatDateToZ(date, false, "month", device?.timezone),
        "MMMyyyy",
        device?.timezone
      );
    }
    return formatDateTimezone(
      formatDateToZ(date, false, "year", device?.timezone),
      "year",
      device?.timezone
    );
  };

  const maxDetail: "month" | "year" =
    filter.calendar === "year" ? "year" : "month";

  const ySuggestedMax = valueScale === "percent" ? 100 : 1;

  return (
    <DeviceCard
      title={t("device.gateway.chart.title")}
      subTitle={getDescription()}
      config={config}
    >
      <div className="ring-black/5 mt-2 flex w-full flex-col gap-3 rounded-2xl bg-white p-10 shadow-sm ring-1">
        <div className="flex flex-wrap items-center justify-end gap-2">
          {GATEWAY_CHART_BUTTONS.map((item) => (
            <GenericButton
              key={item.labelKey}
              text={t(item.labelKey)}
              onClick={() => applyFilterPreset(item.filter)}
              size="sm"
              color="primary"
              variant={
                item.filter.calendar === filter.calendar &&
                item.filter.xFormat === filter.xFormat
                  ? "solid"
                  : "outline"
              }
              rounded="rounded-md"
              tooltip={t(item.tooltipKey)}
              tooltipPlacement="bottom"
            />
          ))}
          <DatePickerField
            id="gateway-chart-date"
            sizes="xs"
            iconOnly
            value={date}
            onChange={(val) => {
              if (val && !Array.isArray(val)) setDate(val as Date);
            }}
            maxDate={new Date()}
            maxDetail={maxDetail}
            color="primary"
          />
        </div>
        <div className="w-full border-t border-gray-200/80 pb-1 pt-3">
          {loading ? (
            <div className="mt-12 flex items-center justify-center">
              <PropagateLoader loading={loading} color="var(--color-500)" />
            </div>
          ) : chartData.datasets.length > 0 &&
            chartData.datasets[0].data.length > 0 ? (
            <BarGraph
              id="gateway-uptime-chart"
              data={chartData}
              config={config}
              height={height}
            />
          ) : (
            <div className="mt-4 flex justify-center">
              <img src={NoDataSvg} alt="" style={{ width: "12%" }} />
            </div>
          )}
        </div>
      </div>
    </DeviceCard>
  );
};

export default GatewayUptimeChart;
